export const RESERVATION_SENSITIVE_DATA_PERMISSION_KEY = 'reservationSensitiveData'

export const RESERVATION_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY = 'reservationSensitiveFieldGroups'

export const RESERVATION_SENSITIVE_FIELD_GROUPS = [
  'guestIdentity',
  'guestCount',
  'contactDetails',
  'financials',
  'bookingChannel',
  'identifiers',
  'notes',
  'customFields',
  'chat',
  'accessLinks',
] as const

export type ReservationSensitiveFieldGroup = (typeof RESERVATION_SENSITIVE_FIELD_GROUPS)[number]

export type ReservationSensitiveFieldGroupAccess = Record<ReservationSensitiveFieldGroup, boolean>

const DEFAULT_ALLOWED_ROLES = ['ADMIN', 'MANAGER'] as const

const ALL_FIELDS_VISIBLE: ReservationSensitiveFieldGroupAccess = {
  accessLinks: true,
  bookingChannel: true,
  chat: true,
  contactDetails: true,
  customFields: true,
  financials: true,
  guestCount: true,
  guestIdentity: true,
  identifiers: true,
  notes: true,
}

const LEGACY_RESTRICTED_FIELDS: ReservationSensitiveFieldGroupAccess = {
  accessLinks: false,
  bookingChannel: false,
  chat: false,
  contactDetails: false,
  customFields: false,
  financials: false,
  // Guest counts were deliberately operational, non-sensitive data before
  // field-level controls existed. Keep that effective access until an admin
  // explicitly changes this field for the role.
  guestCount: true,
  guestIdentity: false,
  identifiers: false,
  notes: false,
}

export function reservationSensitiveFieldGroupsFromMaster(
  enabled: boolean
): ReservationSensitiveFieldGroupAccess {
  return { ...(enabled ? ALL_FIELDS_VISIBLE : LEGACY_RESTRICTED_FIELDS) }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Resolves the role-level master switch. An explicit role/custom-role value
 * wins over the built-in defaults so existing roles keep their current access.
 */
export function canViewReservationSensitiveData(permissions: unknown, role: string): boolean {
  const config = isRecord(permissions) ? permissions : null
  const explicitValue = config?.[RESERVATION_SENSITIVE_DATA_PERMISSION_KEY]
  if (typeof explicitValue === 'boolean') {
    return explicitValue
  }

  return DEFAULT_ALLOWED_ROLES.some((allowedRole) => allowedRole === role)
}

/**
 * Resolve effective field visibility for a role. The master switch gates every
 * historically-sensitive field. Guest count preserves its legacy operational
 * visibility unless an administrator explicitly turns it off.
 *
 * Missing field settings always reproduce the old all-or-nothing behavior.
 */
export function resolveReservationSensitiveFieldGroups(
  permissions: unknown,
  role: string
): ReservationSensitiveFieldGroupAccess {
  const masterEnabled = canViewReservationSensitiveData(permissions, role)
  const access = reservationSensitiveFieldGroupsFromMaster(masterEnabled)
  const config = isRecord(permissions)
    ? permissions[RESERVATION_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY]
    : null
  if (!isRecord(config)) {
    return access
  }

  for (const group of RESERVATION_SENSITIVE_FIELD_GROUPS) {
    const value = config[group]
    if (typeof value !== 'boolean') {
      continue
    }
    if (group === 'guestCount' || masterEnabled) {
      access[group] = value
    }
  }
  return access
}

export function readStoredReservationSensitiveFieldGroups(
  permissions: unknown
): ReservationSensitiveFieldGroupAccess | null {
  const config = isRecord(permissions)
    ? permissions[RESERVATION_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY]
    : null
  if (!isRecord(config)) {
    return null
  }

  const stored = { ...ALL_FIELDS_VISIBLE }
  for (const group of RESERVATION_SENSITIVE_FIELD_GROUPS) {
    if (typeof config[group] === 'boolean') {
      stored[group] = config[group]
    }
  }
  return stored
}

export function nextReservationSensitiveFieldGroups(
  permissions: unknown,
  _role: string,
  group: ReservationSensitiveFieldGroup,
  visible: boolean
): ReservationSensitiveFieldGroupAccess | null {
  const access: ReservationSensitiveFieldGroupAccess = {
    ...(readStoredReservationSensitiveFieldGroups(permissions) ?? ALL_FIELDS_VISIBLE),
    [group]: visible,
  }

  if (RESERVATION_SENSITIVE_FIELD_GROUPS.every((key) => access[key] === ALL_FIELDS_VISIBLE[key])) {
    return null
  }
  return access
}
